import React, { FormEvent, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, LoaderCircleIcon, LockIcon } from "lucide-react";
import { MapleLeaf, MapleWatermark } from "./MapleLeaf";
import { WhatsAppLine } from "./WhatsAppLine";
import { GRADE_OPTIONS, INTAKE_OPTIONS, PAYSTACK_CONFIG } from "../data/payments";
import { payWithPaystack } from "../utils/paystack";
import { EASE } from "../utils/motion";
type Status = {
  kind: "idle";
} | {
  kind: "paying";
} | {
  kind: "paid";
  reference: string;
} | {
  kind: "requested";
} | {
  kind: "error";
  message: string;
};
const field = "w-full border-b border-white/30 bg-transparent py-3 text-base text-white outline-none transition-colors duration-200 placeholder:text-white/40 focus:border-white";
const label = "grid gap-1 text-xs font-semibold tracking-[0.12em] text-white/60";
export function AdmissionFormPayment() {
  const reduce = useReducedMotion();
  const [status, setStatus] = useState<Status>({
    kind: "idle"
  });
  const configured = Boolean(PAYSTACK_CONFIG.publicKey) && PAYSTACK_CONFIG.admissionFormAmountKobo > 0;
  const amount = PAYSTACK_CONFIG.admissionFormAmountKobo / 100;
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const fields = {
      "Parent name": String(data.get("parent") ?? ""),
      "Student name": String(data.get("student") ?? ""),
      Phone: String(data.get("phone") ?? ""),
      Grade: String(data.get("grade") ?? ""),
      Intake: String(data.get("intake") ?? "")
    };
    const email = String(data.get("email") ?? "");
    if (!configured) {
      window.dispatchEvent(new CustomEvent("mcc:conversion", {
        detail: {
          eventName: "admission_form_request",
          ...fields
        }
      }));
      setStatus({
        kind: "requested"
      });
      return;
    }
    setStatus({
      kind: "paying"
    });
    window.dispatchEvent(new CustomEvent("mcc:conversion", {
      detail: {
        eventName: "admission_form_payment_started"
      }
    }));
    try {
      const reference = await payWithPaystack({
        publicKey: PAYSTACK_CONFIG.publicKey,
        email,
        amountKobo: PAYSTACK_CONFIG.admissionFormAmountKobo,
        currency: PAYSTACK_CONFIG.currency,
        fields
      });
      window.dispatchEvent(new CustomEvent("mcc:conversion", {
        detail: {
          eventName: "admission_form_payment_success",
          reference
        }
      }));
      setStatus({
        kind: "paid",
        reference
      });
    } catch (err) {
      const closed = err instanceof Error && err.message === "closed";
      setStatus(closed ? {
        kind: "idle"
      } : {
        kind: "error",
        message: "We couldn’t open the payment window. Please check your connection and try again, or message Admissions."
      });
    }
  }
  return <section id="admission-form" aria-labelledby="admission-form-title" className="relative overflow-hidden bg-[#061f38] py-24 text-white md:py-32" data-id="element-74" data-mp="2qmr5tp429ioy">
      <MapleWatermark className="-left-24 -bottom-32 h-[34rem] w-[34rem]" tone="text-white/[0.05]" rotate={10} data-id="element-75" data-mp="1t73r4y9eitdn" />
      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-5 md:px-9 lg:grid-cols-12" data-id="element-76" data-mp="1y4n5rza0h864">
        <div className="lg:col-span-5" data-id="element-77" data-mp="1ql4hbdi00mq5">
          <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-78" data-mp="1lnksjjnocrjy"><MapleLeaf size={11} data-id="element-79" data-mp="1gohwgopo9vo4" /> ADMISSION FORM</p>
          <h2 id="admission-form-title" className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-80" data-mp="1gn6k40i5vfle">Ready to apply? Get the form online.</h2>
          <p className="mt-6 max-w-md text-base leading-7 text-white/70" data-id="element-81" data-mp="17peczjmlfe93">Pay the admission form fee securely with Paystack: card, bank transfer or USSD. The form is sent to your email once payment is confirmed.</p>
          <dl className="mt-10 border-t border-white/20" data-id="element-82" data-mp="xzbc6x98cw07">
            <div className="flex items-baseline justify-between border-b border-white/20 py-4" data-id="element-83" data-mp="szoy6llkvbqc">
              <dt className="text-sm text-white/60" data-id="element-84" data-mp="o03xa5muktaz">Form fee</dt>
              <dd className="text-2xl font-medium tracking-[-0.04em]" data-id="element-85" data-mp="o2gdad91otle">{amount > 0 ? `₦${amount.toLocaleString("en-NG")}` : "Confirm with Admissions"}</dd>
            </div>
            <div className="flex items-baseline justify-between border-b border-white/20 py-4" data-id="element-86" data-mp="18qpotbihkijm">
              <dt className="text-sm text-white/60" data-id="element-87" data-mp="1dn6c63hv2svn">Payment</dt>
              <dd className="inline-flex items-center gap-2 text-sm" data-id="element-88" data-mp="zxckzeivvwir"><LockIcon size={13} data-id="element-89" data-mp="zwuhpxxt0i1c" /> Secured by Paystack</dd>
            </div>
          </dl>
          <WhatsAppLine label="Questions before paying? Ask Admissions" context="the MCC admission form and fee" className="mt-8" data-id="element-90" data-mp="13x50ycalx4y5" />
        </div>

        <div className="lg:col-span-6 lg:col-start-7" data-id="element-91" data-mp="uxqahcufwudn">
          <AnimatePresence mode="wait" data-id="element-92" data-mp="uzktglm5icf3">
            {status.kind === "paid" || status.kind === "requested" ? <motion.div key="done" role="status" initial={{
            opacity: 0,
            y: reduce ? 0 : 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: reduce ? 0.01 : 0.4,
            ease: EASE
          }} className="border-l-2 border-[#c5233d] pl-6" data-id="element-93" data-mp="h0bhjy4za4gx">
                <CheckIcon size={24} className="text-[#f0b1b9]" data-id="element-94" data-mp="2fe4nera9wpk9" />
                <p className="mt-4 text-3xl font-medium tracking-[-0.05em]" data-id="element-95" data-mp="3qhqq9va7qjp">{status.kind === "paid" ? "Payment received." : "Request received."}</p>
                <p className="mt-4 max-w-md text-base leading-7 text-white/70" data-id="element-96" data-mp="3vvlinqibt0t2">
                  {status.kind === "paid" ? "Thank you. MCC Admissions will verify your payment and email the admission form with next steps." : "Online payment is being finalised. MCC Admissions will contact you with the admission form and payment details."}
                </p>
                {status.kind === "paid" && <p className="mt-6 text-xs tracking-[0.12em] text-white/50" data-id="element-97" data-mp="ihi8x6ltyyt1">REFERENCE: {status.reference}</p>}
              </motion.div> : <motion.form key="form" onSubmit={submit} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: reduce ? 0.01 : 0.25
          }} className="grid gap-6" data-id="element-98" data-mp="rfu47lxn6krt">
                <div className="grid gap-6 sm:grid-cols-2" data-id="element-99" data-mp="2t8ie2e4sk6gk">
                  <label className={label} data-id="element-100" data-mp="x4amdzygybdy">PARENT / GUARDIAN<input name="parent" required autoComplete="name" className={field} placeholder="Full name" data-id="element-101" data-mp="x5c8czh0wiag" /></label>
                  <label className={label} data-id="element-102" data-mp="123x9d143n5zy">STUDENT’S NAME<input name="student" required className={field} placeholder="Full name" data-id="element-103" data-mp="172i1sed40ldu" /></label>
                </div>
                <div className="grid gap-6 sm:grid-cols-2" data-id="element-104" data-mp="1b4lfl9n9422a">
                  <label className={label} data-id="element-105" data-mp="1b1z9leg0dum6">EMAIL<input name="email" type="email" required autoComplete="email" className={field} placeholder="Where we send the form" data-id="element-106" data-mp="1g3g9b6mrbb16" /></label>
                  <label className={label} data-id="element-107" data-mp="1l0orh77wnf2f">PHONE / WHATSAPP<input name="phone" type="tel" required autoComplete="tel" className={field} placeholder="080…" data-id="element-108" data-mp="1pszoa17cmkqr" /></label>
                </div>
                <div className="grid gap-6 sm:grid-cols-2" data-id="element-109" data-mp="1uqri50rk0gxl">
                  <label className={label} data-id="element-110" data-mp="1ytxxqz0k3kgq">ENTRY GRADE
                    <select name="grade" required defaultValue="" className={`${field} [&>option]:text-[#061f38]`} data-id="element-111" data-mp="1yvheeqq4jxj1">
                      <option value="" disabled data-id="element-112" data-mp="23qejxno73or6">Select</option>
                      {GRADE_OPTIONS.map(g => <option key={g} data-id="element-113" data-mp="3o8p2be1v9art">{g}</option>)}
                    </select>
                  </label>
                  <label className={label} data-id="element-114" data-mp="3jdtaof0jkpf8">INTAKE
                    <select name="intake" required defaultValue="" className={`${field} [&>option]:text-[#061f38]`} data-id="element-115" data-mp="3f9bu0ophas8o">
                      <option value="" disabled data-id="element-116" data-mp="3acbu6vj82lq0">Select</option>
                      {INTAKE_OPTIONS.map(g => <option key={g} data-id="element-117" data-mp="auu68o9bii41">{g}</option>)}
                    </select>
                  </label>
                </div>
                {status.kind === "error" && <p role="alert" className="border-l-2 border-[#f0b1b9] pl-4 text-sm text-[#f0b1b9]" data-id="element-118" data-mp="23lgg21bwbh6">{status.message}</p>}
                <button type="submit" disabled={status.kind === "paying"} className="group mt-2 inline-flex w-fit items-center gap-2 bg-[#c5233d] px-6 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#a71931] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-119" data-mp="3taehn5w4galp">
                  {status.kind === "paying" ? <LoaderCircleIcon size={15} className="animate-spin" data-id="element-120" data-mp="3u8w572l37rax" /> : <MapleLeaf size={13} className="transition-transform duration-300 group-hover:rotate-[-18deg]" data-id="element-121" data-mp="2wl3kp6exe6l" />}
                  {configured ? status.kind === "paying" ? "Opening Paystack…" : "Pay & get the form" : "Request the admission form"}
                </button>
                <p className="text-xs text-white/45" data-id="element-122" data-mp="gwcbc86c5yc5">Your details are used only to process this application. The admission form fee is non-refundable.</p>
              </motion.form>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}